import React from 'react';
import styled from 'styled-components';

interface MatchNavProps {
  /** 1-based index of the currently-active match, displayed as "current / total". */
  current: number;
  /** Total number of matches. */
  total: number;
  onPrev: () => void;
  onNext: () => void;
  /** aria-label for the prev button (e.g. "Previous pull quote"). */
  prevLabel: string;
  /** aria-label for the next button. */
  nextLabel: string;
  /**
   * Border + text color. Omitted → a neutral design-system strip (`--border`
   * outline, `--text-secondary` text) — used for the topic-match nav. Pass a
   * content color (e.g. `--content-quotes`) for the quotes/clips strips. The
   * buttons inherit the color so the hover tint matches.
   */
  color?: string;
  /**
   * Fused rendering (PLAT-11039): no outline, `background` fill, square left
   * corners — for attaching flush to the right edge of the toggle/filter pill
   * that owns the matches, so toggle + nav read as one control. `color` is the
   * text color in this mode. The counter reserves the width of "total / total"
   * so flipping between inactive and active never changes the pill's size.
   */
  attached?: boolean;
  /** Background fill for the attached variant (e.g. `--content-quotes-bg`). */
  background?: string;
  /**
   * Resting state for the attached variant (PLAT-11039): the host toggle is
   * off, so the zone renders the same n/N dimmed (muted colors come from the
   * call site) and the arrows fire `onActivate` instead of navigating.
   * Identical text in both states keeps the pill's footprint fixed —
   * toggling changes color, never geometry.
   */
  inactive?: boolean;
  /** Called when an arrow is clicked while `inactive` (e.g. turn the toggle on). */
  onActivate?: () => void;
  /** Forwarded to the root for parent overrides via `styled(MatchNav)`. */
  className?: string;
}

/**
 * Shared prev/next/count navigation strip used by transcript views
 * (quotes, clips, topic matches). Renders the active index and total as
 * `current / total` between ‹ and › buttons.
 */
export const MatchNav: React.FC<MatchNavProps> = ({
  current,
  total,
  onPrev,
  onNext,
  prevLabel,
  nextLabel,
  color,
  attached,
  background,
  inactive,
  onActivate,
  className,
}) => {
  if (total <= 0) return null;
  const handlePrev = inactive ? onActivate : onPrev;
  const handleNext = inactive ? onActivate : onNext;
  return (
    <Wrapper
      $color={color}
      $attached={attached}
      $background={background}
      className={className}
    >
      <NavButton
        type="button"
        onClick={handlePrev}
        aria-label={prevLabel}
        $dim={inactive}
        $compact={attached}
      >
        ‹
      </NavButton>
      <Counter $digits={attached ? String(total).length : undefined}>
        {/* Attached zones use the tight n/N format so the reserved box stays
            narrow; the standalone strip keeps the roomier "n / N". Inactive
            zones show the same n/N, just dimmed — a lone total floating in
            the reserved box read as a different (confusing) control. */}
        {attached ? `${current}/${total}` : `${current} / ${total}`}
      </Counter>
      <NavButton
        type="button"
        onClick={handleNext}
        aria-label={nextLabel}
        $dim={inactive}
        $compact={attached}
      >
        ›
      </NavButton>
    </Wrapper>
  );
};

const Wrapper = styled.div<{
  $color?: string;
  $attached?: boolean;
  $background?: string;
}>`
  display: inline-flex;
  align-items: center;
  gap: ${p => (p.$attached ? '1px' : '2px')};
  /* Attached zones sit flush against the host label — no divider, minimal
     left padding — so label + counter read as one word-group (PLAT-11039). */
  padding: ${p => (p.$attached ? '1px 3px 1px 1px' : '1px 3px')};
  border-radius: ${p =>
    p.$attached ? '0 var(--radius-button) var(--radius-button) 0' : '6px'};
  border: ${p =>
    p.$attached ? 'none' : `1px solid ${p.$color || 'var(--border)'}`};
  background: ${p => (p.$attached && p.$background) || 'transparent'};
  color: ${p => p.$color || 'var(--text-secondary)'};
  /* Attached zones match the host label's type (var(--text-xs) medium, same
     as the language trigger) so the numbers read as part of the same
     control. */
  font-size: ${p => (p.$attached ? 'var(--text-xs)' : '11px')};
  font-weight: ${p => (p.$attached ? 'var(--font-weight-medium)' : '500')};
`;

const NavButton = styled.button<{ $dim?: boolean; $compact?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${p => (p.$compact ? '13px' : '16px')};
  height: ${p => (p.$compact ? '13px' : '16px')};
  border: none;
  border-radius: ${p => (p.$compact ? '3px' : '4px')};
  background: transparent;
  color: inherit;
  font-size: ${p => (p.$compact ? '11px' : '13px')};
  line-height: 1;
  cursor: pointer;
  padding: 0;
  opacity: ${p => (p.$dim ? 0.45 : 1)};

  &:hover {
    background-color: color-mix(in srgb, currentColor 18%, transparent);
    opacity: 1;
  }
`;

/*
 * `$digits` (attached mode) reserves the width of the widest state,
 * "total/total", in tabular-nums ch units — digits × 2 plus 1ch for the
 * slash — so the inactive total-only view and the active "n/N" view
 * occupy exactly the same box and the pill never resizes.
 */
const Counter = styled.span<{ $digits?: number }>`
  min-width: ${p => (p.$digits ? `${p.$digits * 2 + 1}ch` : '26px')};
  text-align: center;
  font-variant-numeric: tabular-nums;
  user-select: none;
`;
